/*
  Everything that differs between the ten platform landing pages — /chrome,
  /figma and the rest. They were ten near-identical files before this: chrome
  and brave differed on sixteen lines out of 312, all of them strings.

  This is also the single source for the download URLs. They previously lived
  in three places that had already drifted apart: the landing pages, the switch
  in templates/download, and the hardcoded anchors in free-download-platforms.
  templates/download had no Firefox case at all, so /firefox/download/ shipped
  its primary call to action with an empty href.

  Layout lives in components/templates/platform-landing, not here. The only
  per-platform deviations that survived — FigJam's narrower hero copy and
  mirrored second section — are optional fields below rather than forks of
  the template.
*/
export interface PlatformSection {
  eyebrow: string;
  headline: string;
  body: string;
  image: { src: string; alt: string; className: string };
}

export interface Platform {
  /** Display name, as it appears in "Stark for {name}". */
  name: string;
  /** Route segment, and the key in PLATFORMS. */
  slug: string;
  /** The `#download--*` id the analytics call binds to. */
  trackId: string;
  /** Tail of the download button label. */
  downloadLabel: string;
  downloadUrl: string;
  /** Logo for the free-download grid. FigJam has never had one. */
  iconSrc?: string;
  eyebrow: string;
  heroBody: string;
  /** FigJam sets its own; everything else takes the template default. */
  heroBodyClassName?: string;
  heroImage: { src: string; alt: string };
  /** Body copy of step 2 in the closing three-step grid. */
  step2Body: string;
  /** FigJam mirrors the second section. */
  sectionTwoAlign?: 'start' | 'end';
  sections: PlatformSection[];
}

export const PLATFORMS: Record<string, Platform> = {
  chrome: {
    name: 'Chrome',
    slug: 'chrome',
    trackId: 'chrome',
    downloadLabel: 'Chrome',
    downloadUrl:
      'https://chrome.google.com/webstore/detail/stark/fkfaapnmfippddbeemjjbclenphooipm',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249161/marketing/platforms/chrome_lwu7fo.png',
    eyebrow: 'Work with Stark in your favorite browser',
    heroBody:
      'Make every project accessible from the start. Stark for Chrome keeps you focused on the parts of a project that need an accessibility audit. With powerful tools, automated checks, and smart suggestions you can supercharge your accessibility workflow at scale and in record time.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258380/marketing/headers/chrome_qbakuc.webp',
      alt: 'Stark for Chrome shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Chrome and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: "Stark simplifies and accelerates collaboration on accessibility issues between designers and developers. With Stark's browser extension, you can scan for issues and get a Live Preview of our color and typography suggestions.",
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248871/marketing/highlights/platform__live-preview_nonzpr.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'max-w-138',
        },
      },
    ],
  },
  brave: {
    name: 'Brave',
    slug: 'brave',
    trackId: 'brave',
    downloadLabel: 'Brave',
    downloadUrl:
      'https://chrome.google.com/webstore/detail/stark/fkfaapnmfippddbeemjjbclenphooipm',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249160/marketing/platforms/brave_kkitze.png',
    eyebrow: 'Work with Stark in your favorite browser',
    heroBody:
      'Make every project accessible from the start. Stark for Brave keeps you focused on the parts of a project that need an accessibility audit. With powerful tools, automated checks, and smart suggestions you can supercharge your accessibility workflow at scale and in record time.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258376/marketing/headers/brave_tigdgl.webp',
      alt: 'Stark for Brave shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Brave and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: "Stark simplifies and accelerates collaboration on accessibility issues between designers and developers. With Stark's browser extension, you can scan for issues and get a Live Preview of our color and typography suggestions.",
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248871/marketing/highlights/platform__live-preview_nonzpr.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'max-w-138',
        },
      },
    ],
  },
  edge: {
    name: 'Edge',
    slug: 'edge',
    trackId: 'edge',
    downloadLabel: 'Edge',
    downloadUrl:
      'https://microsoftedge.microsoft.com/addons/detail/stark/idpodoagbkllmpdjdepbmlefgiblmnhl',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249163/marketing/platforms/edge_vrh93l.png',
    eyebrow: 'Work with Stark in your favorite browser',
    heroBody:
      'Make every project accessible from the start. Stark for Edge keeps you focused on the parts of a project that need an accessibility audit. With powerful tools, automated checks, and smart suggestions you can supercharge your accessibility workflow at scale and in record time.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258388/marketing/headers/edge_fdoj7d.webp',
      alt: 'Stark for Edge shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Edge and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: "Stark simplifies and accelerates collaboration on accessibility issues between designers and developers. With Stark's browser extension, you can scan for issues and get a Live Preview of our color and typography suggestions.",
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248871/marketing/highlights/platform__live-preview_nonzpr.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'max-w-138',
        },
      },
    ],
  },
  firefox: {
    name: 'Firefox',
    slug: 'firefox',
    trackId: 'firefox',
    downloadLabel: 'Firefox',
    downloadUrl:
      'https://addons.mozilla.org/en-US/firefox/addon/stark-accessibility-checker/',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249177/marketing/platforms/firefox_fkg34x.png',
    eyebrow: 'Work with Stark in your favorite browser',
    heroBody:
      'Make every project accessible from the start. Stark for Firefox keeps you focused on the parts of a project that need an accessibility audit. With powerful tools, automated checks, and smart suggestions you can supercharge your accessibility workflow at scale and in record time.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258401/marketing/headers/firefox_wgj5e5.webp',
      alt: 'Stark for Firefox shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Firefox and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: "Stark simplifies and accelerates collaboration on accessibility issues between designers and developers. With Stark's browser extension, you can scan for issues and get a Live Preview of our color and typography suggestions.",
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248871/marketing/highlights/platform__live-preview_nonzpr.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'max-w-138',
        },
      },
    ],
  },
  safari: {
    name: 'Safari',
    slug: 'safari',
    trackId: 'safari',
    downloadLabel: 'Safari',
    downloadUrl: 'https://apps.apple.com/us/app/stark-for-safari/id6444031666',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249181/marketing/platforms/safari_ornox9.png',
    eyebrow: 'Work with Stark in your favorite browser',
    heroBody:
      'Make every project accessible from the start. Stark for Safari keeps you focused on the parts of a project that need an accessibility audit. With powerful tools, automated checks, and smart suggestions you can supercharge your accessibility workflow at scale and in record time.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258470/marketing/headers/safari_jdjbiv.webp',
      alt: 'Stark for Safari shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Safari and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: "Stark simplifies and accelerates collaboration on accessibility issues between designers and developers. With Stark's browser extension, you can scan for issues and get a Live Preview of our color and typography suggestions.",
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248871/marketing/highlights/platform__live-preview_nonzpr.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'max-w-138',
        },
      },
    ],
  },
  arc: {
    name: 'Arc',
    slug: 'arc',
    trackId: 'arc',
    downloadLabel: 'Arc',
    downloadUrl:
      'https://chrome.google.com/webstore/detail/stark/fkfaapnmfippddbeemjjbclenphooipm',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249158/marketing/platforms/arc_wcrtuz.png',
    eyebrow: 'Work with Stark in your favorite browser',
    heroBody:
      'Make every project accessible from the start. Stark for Arc keeps you focused on the parts of a project that need an accessibility audit. With powerful tools, automated checks, and smart suggestions you can supercharge your accessibility workflow at scale and in record time.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258372/marketing/headers/arc_v0rmwt.webp',
      alt: 'Stark for Arc shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Arc and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: "Stark simplifies and accelerates collaboration on accessibility issues between designers and developers. With Stark's browser extension, you can scan for issues and get a Live Preview of our color and typography suggestions.",
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248871/marketing/highlights/platform__live-preview_nonzpr.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'max-w-138',
        },
      },
    ],
  },
  sketch: {
    name: 'Sketch',
    slug: 'sketch',
    trackId: 'sketch',
    downloadLabel: 'Sketch',
    downloadUrl: '/sketch/download/',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249183/marketing/platforms/sketch_dgg7lp.png',
    eyebrow: 'Work with Stark in your favorite design tool',
    heroBody:
      'Make every project accessible from the start. Stark for Sketch keeps you focused on the parts of a project that need an accessibility audit. Whether you’re a team of 1 or 1,000, you have everything you need to make anything accessible from design to code.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258474/marketing/headers/sketch_p4fu57.webp',
      alt: 'Stark for Sketch shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Sketch and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: 'Once you open the Stark plugin in Sketch or Figma it automagically connects to Stark. So you can apply for example color suggestions to individual design elements or rectify all instances of an issue across your entire file or design system with just a click. That’s powerful!',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248869/marketing/highlights/platform__connected_v9br7f.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'w-138',
        },
      },
    ],
  },
  figma: {
    name: 'Figma',
    slug: 'figma',
    trackId: 'figma',
    downloadLabel: 'Figma',
    downloadUrl: 'https://www.figma.com/community/plugin/732603254453395948',
    iconSrc:
      'https://res.cloudinary.com/stark-lab/image/upload/v1761249164/marketing/platforms/figma_luu0sa.png',
    eyebrow: 'Work with Stark in your favorite design tool',
    heroBody:
      'Make every project accessible from the start. Stark for Figma keeps you focused on the parts of a project that need an accessibility audit. Whether you’re a team of 1 or 1,000, you have everything you need to make anything accessible from design to code.',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258396/marketing/headers/figma_vplw2k.webp',
      alt: 'Stark for Figma shown with the main menu and contrast features popping out. The Contrast feature shows a red and white color pairining not passing AAA standards with 2 rows of suggested colors to remedy. The main menu shows the full suite of Stark tools including Contrast, Typography, Vision Simulator, Focus Order, Landmarks, and Touch Targets.',
    },
    step2Body:
      'Download and install Stark for Figma and begin your accessibility journey with a Contrast Checker and Vision Simulator.',
    sections: [
      {
        eyebrow: 'Meet your new superpower',
        headline: 'Create and test accessible designs in record time',
        body: 'Whether you’re building a website, online shop, mobile app, or SaaS product, Stark gives every designer, engineer, PM, and QA expert the manual and automated tools to make it accessible with ease.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248848/marketing/highlights/homepage__superpower_nphq4p.webp',
          alt: 'A website about plants that is overlayed with Stark annotations showing Accessibility issues to address.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Your ACcessibility ToolBOX',
        headline: 'Everything you need, right where you need it',
        body: 'From Contrast Checker with Color Suggestions over Alt-Text Annotations to Focus Order and more, Stark offers the fully integrated and automated tools to help you and your team design, build and test software that meets the latest accessibility standards right in your design app and browser of choice.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248873/marketing/highlights/platform__toolbox_iuyd2q.webp',
          alt: 'The Stark main menu listing Contrast, Typography, Alt-Text, Touch Targets, Focus Order, landmarks, and Vision Simulator as available tools.',
          className: 'w-full max-w-138 scale-150',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices on the fly and get tips on how to fix any issues we find. We’ll take you from novice to expert while doing the heavy lifting in the background.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248875/marketing/highlights/platform__train_ld33lp.webp',
          alt: 'The Stark Alt-Text creator showing instructions for when to add Alt-Text as well as two created annotations: one with the text written and another showing as decorative.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Retrofitting at scale with a click',
        headline: 'Accessibility easy as one, two, three',
        body: 'Once you open the Stark plugin in Sketch or Figma it automagically connects to Stark. So you can apply for example color suggestions to individual design elements or rectify all instances of an issue across your entire file or design system with just a click. That’s powerful!',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248869/marketing/highlights/platform__connected_v9br7f.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'w-138',
        },
      },
    ],
  },
  figjam: {
    name: 'FigJam',
    slug: 'figjam',
    trackId: 'figjam',
    downloadLabel: 'FigJam',
    downloadUrl: 'https://www.figma.com/community/widget/1030161589655245054',
    eyebrow: 'Work with Stark in your favorite design tool',
    heroBody:
      'With Stark for FigJam, your team’s accessibility conversations start at the whiteboard. Keep track of everyone’s tasks, share and discuss feedback, and learn best practices for building the most accessible products as you go.',
    heroBodyClassName: 'mt-4 mb-8 max-w-131.25',
    heroImage: {
      src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761258392/marketing/headers/figjam_ehkj8w.webp',
      alt: 'Stark for FigJam shown with the discipline UI showing Designers, Engineers, and Product Managers. To the right, a popup shows the Product Managers checklist with the General category selected and expanded. All of this sits on top of a FigJam file open to a plants website.',
    },
    step2Body:
      'Download and install Stark for FigJam and begin your accessibility journey with a checklist for every discipline.',
    sectionTwoAlign: 'start',
    sections: [
      {
        eyebrow: 'Your accessibility to-do list',
        headline: 'Role-specific checklists for better team collaboration',
        body: 'Accessibility Checklists in Figma and FigJam are tailored to a designer’s engineer’s, and product manager’s expertise, so you can ensure you’re solving for each task from different angles.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248838/marketing/highlights/figjam__role-checklist_ngslnv.webp',
          alt: "A FigJam screen shown with an overlay of Stark's checklists allowing the user to click Designers, Engineers and Product Managers.",
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Keep your team on track',
        headline: 'Check off your tasks, track your team’s progress',
        body: 'Stark’s Accessibility Checklists tell you exactly what requirements you should meet before marking a task as complete.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248839/marketing/highlights/figjam__team-on-track_w2afg2.webp',
          alt: 'The Stark Project Manager checklist showing the General category expanded. Two users have added comments and there is a link to learn more about General requirements for Project Managers.',
          className: 'w-full max-w-138',
        },
      },
      {
        eyebrow: 'Train on the job',
        headline: 'Learn as you go',
        body: 'Stark is your accessibility sidekick. Learn best practices for meeting requirements to complete each task, making it easy to get started and get done.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248841/marketing/highlights/figjam__train_zhsgs5.webp',
          alt: 'The Color & Contrast popup of the Stark Checklist shown with a bulleted list of things a designer can do to ensure they are adhering to good contrast standards.',
          className: 'w-full max-w-138.5',
        },
      },
      {
        eyebrow: 'Brainstorm accessible solutions',
        headline: 'Share feedback from start to finish',
        body: 'FigJam makes it easy for teams to leave comments during whiteboard sessions. With Accessibility Checklists, you can add sticky notes to tasks so everyone can see thoughts, questions, and comments left about the UI.',
        image: {
          src: 'https://res.cloudinary.com/stark-lab/image/upload/v1761248837/marketing/highlights/figjam__brainstorm_bxdcmx.webp',
          alt: 'The Stark Typography analyzer showing font details for a design component with options to remedy it by increasing the size.',
          className: 'w-138',
        },
      },
    ],
  },
};

/** Order the free-download grid renders in. FigJam is absent by design. */
export const DOWNLOAD_GRID_ORDER = [
  'figma',
  'sketch',
  'chrome',
  'firefox',
  'edge',
  'safari',
  'arc',
  'brave',
];
